// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { z } from 'zod';
import {
  CompanySubcategory,
  IlliquidAssetCurrency,
  IlliquidAssetType,
  OtherIlliquidSubcategory,
  PeVcSubcategory,
  RealEstateSubcategory,
  TangibleSubcategory,
} from '@shared/data-access/types/engine-types';

export const illiquidAssetFormSchema = z.discriminatedUnion('category', [
  z.object({
    category: z.literal(IlliquidAssetType.RealEstate),
    name: z.string().min(1),
    subcategory: z.enum(RealEstateSubcategory),
    description: z.union([z.string(), z.null()]).optional(),
    currency: z.enum(IlliquidAssetCurrency),
    current_valuation: z.number().positive(),
    valuation_date: z.string().min(1),
    acquisition_date: z.string().min(1),
    acquisition_value: z.number().positive(),
    ownership_pct: z.number().min(0).max(1),
  }),

  z.object({
    category: z.literal(IlliquidAssetType.Company),
    name: z.string().min(1),
    subcategory: z.enum(CompanySubcategory),
    description: z.union([z.string(), z.null()]).optional(),
    currency: z.enum(IlliquidAssetCurrency),
    current_valuation: z.number().positive(),
    valuation_date: z.string().min(1),
    acquisition_date: z.string().min(1),
    acquisition_value: z.number().positive(),
    ownership_pct: z.number().min(0).max(1),
  }),

  z.object({
    category: z.literal(IlliquidAssetType.Tangible),
    name: z.string().min(1),
    subcategory: z.enum(TangibleSubcategory),
    description: z.union([z.string(), z.null()]).optional(),
    currency: z.enum(IlliquidAssetCurrency),
    current_valuation: z.number().positive(),
    valuation_date: z.string().min(1),
    acquisition_date: z.string().min(1),
    acquisition_value: z.number().positive(),
    ownership_pct: z.number().min(0).max(1),
  }),

  z.object({
    category: z.literal(IlliquidAssetType.OtherIlliquid),
    name: z.string().min(1),
    subcategory: z.enum(OtherIlliquidSubcategory),
    description: z.union([z.string(), z.null()]).optional(),
    currency: z.enum(IlliquidAssetCurrency),
    current_valuation: z.number().positive(),
    valuation_date: z.string().min(1),
    acquisition_date: z.string().min(1),
    acquisition_value: z.number().positive(),
    ownership_pct: z.number().min(0).max(1),
  }),

  z.object({
    category: z.literal(IlliquidAssetType.PeVc),
    name: z.string().min(1),
    subcategory: z.enum(PeVcSubcategory),
    description: z.union([z.string(), z.null()]).optional(),
    currency: z.enum(IlliquidAssetCurrency),
    current_valuation: z.number().positive(),
    valuation_date: z.string().min(1),
    fund_manager: z.string().min(1),
    total_commitment: z.number().min(0),
  }),
]);

export type IlliquidAssetFormData = z.infer<typeof illiquidAssetFormSchema>;
